<template>
  <v-tooltip location="bottom">
    <template #activator="{ props: tooltipProps }">
      <v-btn
        icon
        size="small"
        :width="width"
        :height="height"
        :elevation="elevation"
        :class="btnClasses"
        :color="btnColor || undefined"
        :variant="btnColor ? 'flat' : 'elevated'"
        rounded="circle"
        v-bind="mergedActivatorProps(tooltipProps)"
      >
        <v-icon
          :size="iconSize"
          :color="resolvedIconColor || undefined"
        >{{ icon }}</v-icon>
      </v-btn>
    </template>
    <span>{{ label }}</span>
  </v-tooltip>
</template>

<script>
import { normalizeVuetifyColor, parseBtnClass } from '@/helpers/vuetifyColor';

const COLORED_BUTTONS = new Set(['primary', 'red', 'blue', 'error', 'secondary']);

export default {
  name: 'BaseViewHeaderBtn',

  inheritAttrs: false,

  props: {
    label: {
      type: String,
      default: '',
    },
    icon: {
      type: String,
      default: '',
    },
    color: {
      type: String,
      default: 'background',
    },
    small: {
      type: Boolean,
      default: false,
    },
    xsmall: {
      type: Boolean,
      default: false,
    },
    elevation: {
      type: [Number, String],
      default: 1,
    },
    iconColor: {
      type: String,
      default: '',
    },
  },

  computed: {
    normalizedColor() {
      if (!this.$store.state.isDarkMode) {
        return this.color;
      }
      return normalizeVuetifyColor(`${this.color} lighten-1`);
    },

    btnClasses() {
      const classes = [];
      const parts = this.normalizedColor.split(/\s+/).filter(Boolean);

      parts.forEach((part) => {
        if (part.includes('--text')) {
          classes.push(part);
        }
      });

      if (parts.includes('background') || this.color.trim() === 'background') {
        classes.push('background');
      }

      if (!this.xsmall) {
        classes.push('ma-1');
      }

      return classes.join(' ');
    },

    btnColor() {
      const parts = this.normalizedColor.split(/\s+/).filter((part) => !part.includes('--text'));
      const base = parts[0];

      if (!base || base === 'background' || base === 'white') {
        return undefined;
      }

      return parseBtnClass(normalizeVuetifyColor(parts.join(' '))).color;
    },

    width() {
      if (this.xsmall) {
        return 12;
      }
      if (this.small) {
        return 20;
      }
      return 28;
    },

    height() {
      if (this.xsmall) {
        return 12;
      }
      if (this.small) {
        return 20;
      }
      return 28;
    },

    iconSize() {
      if (this.small || this.xsmall) {
        return 'x-small';
      }
      return 'small';
    },

    resolvedIconColor() {
      if (this.iconColor) {
        return this.iconColor;
      }

      const parts = this.normalizedColor.split(/\s+/);
      if (parts.some((part) => part.includes('--text'))) {
        return 'white';
      }

      const base = parts.find((part) => !part.includes('--text') && !part.startsWith('lighten-') && !part.startsWith('darken-'));
      if (base && COLORED_BUTTONS.has(base)) {
        return 'white';
      }

      return '';
    },
  },

  methods: {
    mergedActivatorProps(tooltipProps) {
      const { onClick: tooltipClick, ...restTooltipProps } = tooltipProps;
      const { onClick: attrClick, ...restAttrs } = this.$attrs;

      return {
        ...restTooltipProps,
        ...restAttrs,
        onClick: (event) => {
          if (typeof tooltipClick === 'function') {
            tooltipClick(event);
          }
          if (typeof attrClick === 'function') {
            attrClick(event);
          } else if (Array.isArray(attrClick)) {
            attrClick.forEach((handler) => handler(event));
          }
        },
      };
    },
  },
};
</script>
